/**
 * E2EE Audio Tunnel Service
 *
 * Encrypted voice calls over WebSocket — no WebRTC/TURN/SIP involved.
 * All traffic is normal HTTPS WebSocket. Server can't decrypt audio (E2EE).
 *
 * Key exchange: X25519 between peers via server relay, authenticated by a
 *   long-term Ed25519 identity key with TOFU pinning (ADR-014). A v1 peer
 *   (old native build) or an anonymous guest downgrades to the v1 KDF and is
 *   reported as unauthenticated rather than silently trusted.
 * KDF: SHA-512, tag "VoxioxTunnelV2" (identity keys mixed in) or "VoxioxTunnelV1".
 * Audio encryption: NaCl secretbox (XSalsa20-Poly1305) per 20ms chunk.
 * Nonce: 20-byte direction-separated prefix + 4-byte counter = 24 bytes.
 * Compression: G.711 mu-law (1 byte/sample vs 2 for PCM).
 * Transport: Binary WebSocket frames (no base64/JSON for audio).
 * DPI resistance: constant-traffic padding (comfort frames during silence).
 * SAS: 4-emoji Short Authentication String (passive, no forced verification).
 *
 * Padded frame format (inside secretbox):
 *   [flags: 1B][mu-law: 960B][pad: 63B] = 1024B plaintext
 *   flags 0x01 = audio, 0x00 = comfort (receiver discards)
 * Wire frame: [0x01 tag][counter 4B LE][ciphertext 1040B] = 1045B total
 */
import nacl from 'tweetnacl';
import { secureSyncStorage } from './secureStore.mjs';
import { wsService } from './ws';
import { ReplayWindow } from './replayWindow.mjs';
import { Identity, TofuStore, authenticatePeer, b64enc as idB64enc } from './identity.mjs';
import { serverWsBase } from './serverBase.mjs';
import { deriveSession } from './tunnelKdf.mjs';
import {
  selectCodec, buildPlaintext, parsePlaintext,
  FRAME_AUDIO as FLAG_AUDIO, FRAME_COMFORT as FLAG_COMFORT,
} from './codecNegotiation.mjs';
import { isOpusSupported, createOpusEncoder, createOpusDecoder } from './opusCodec.mjs';

// ADR-014: one device identity + one pin store for the whole app
// Not localStorage: a WebView may evict that, and an evicted identity makes
// this device a stranger to every contact (ADR-016, astrocall-qvt).
export const identity = new Identity(secureSyncStorage);
export const tofu = new TofuStore(secureSyncStorage);

// ─── Native logcat bridge (Android) ─────────────────────────────────────────
function nlog(msg) {
  console.log(msg);
  if (window.VoxioxNative?.log) window.VoxioxNative.log(msg);
}

// ─── Byte helpers ───────────────────────────────────────────────────────────
function b64enc(u8) { return btoa(String.fromCharCode(...u8)); }
function b64dec(s) { return Uint8Array.from(atob(s), c => c.charCodeAt(0)); }

// ─── Nonce: 20-byte prefix + 4-byte counter LE ─────────────────────────────
function buildNonce(prefix, counter) {
  const n = new Uint8Array(24);
  n.set(prefix);
  n[20] = counter & 0xff;
  n[21] = (counter >> 8) & 0xff;
  n[22] = (counter >> 16) & 0xff;
  n[23] = (counter >> 24) & 0xff;
  return n;
}

// ─── Audio encoding: Float32 <-> Int16 ──────────────────────────────────────
function f32toI16(f) {
  const r = new Int16Array(f.length);
  for (let i = 0; i < f.length; i++) {
    const s = Math.max(-1, Math.min(1, f[i]));
    r[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
  }
  return r;
}
function i16toF32(d) {
  const r = new Float32Array(d.length);
  for (let i = 0; i < d.length; i++) r[i] = d[i] / (d[i] < 0 ? 0x8000 : 0x7fff);
  return r;
}

// ─── G.711 mu-law codec (ITU standard) ─────────────────────────────────────
/** How long a dropped tunnel socket keeps trying to come back — covers a server restart, not only a blip. */
const RECONNECT_WINDOW_MS = 30000;
const MULAW_BIAS = 0x84;
const MULAW_CLIP = 32635;

function linearToMulaw(sample) {
  let sign = (sample >> 8) & 0x80;
  if (sign) sample = -sample;
  if (sample > MULAW_CLIP) sample = MULAW_CLIP;
  sample += MULAW_BIAS;
  let exponent = 7;
  for (let expMask = 0x4000; (sample & expMask) === 0 && exponent > 0; exponent--, expMask >>= 1) {}
  const mantissa = (sample >> (exponent + 3)) & 0x0f;
  return (~(sign | (exponent << 4) | mantissa)) & 0xff;
}

function mulawToLinear(mulaw) {
  mulaw = ~mulaw & 0xff;
  const sign = mulaw & 0x80;
  const exponent = (mulaw >> 4) & 0x07;
  const mantissa = mulaw & 0x0f;
  let sample = ((mantissa << 3) + MULAW_BIAS) << exponent;
  sample -= MULAW_BIAS;
  return sign ? -sample : sample;
}

function encodeMulaw(int16arr) {
  const out = new Uint8Array(int16arr.length);
  for (let i = 0; i < int16arr.length; i++) out[i] = linearToMulaw(int16arr[i]);
  return out;
}

function decodeMulaw(mulawArr) {
  const out = new Int16Array(mulawArr.length);
  for (let i = 0; i < mulawArr.length; i++) out[i] = mulawToLinear(mulawArr[i]);
  return out;
}

// ─── SAS (Short Authentication String) — 4 emoji from 256-emoji palette ────
const SAS_EMOJI = [
  '\u{1F436}','\u{1F431}','\u{1F42D}','\u{1F439}','\u{1F430}','\u{1F98A}','\u{1F43B}','\u{1F43C}',
  '\u{1F428}','\u{1F42F}','\u{1F981}','\u{1F984}','\u{1F42E}','\u{1F437}','\u{1F438}','\u{1F435}',
  '\u{1F414}','\u{1F427}','\u{1F426}','\u{1F989}','\u{1F987}','\u{1F40A}','\u{1F422}','\u{1F40D}',
  '\u{1F432}','\u{1F995}','\u{1F433}','\u{1F42C}','\u{1F41F}','\u{1F420}','\u{1F419}','\u{1F41A}',
  '\u{1F40C}','\u{1F98B}','\u{1F41B}','\u{1F41D}','\u{1F41E}','\u{1F997}','\u{1F577}','\u{1F982}',
  '\u{1F490}','\u{1F339}','\u{1F33B}','\u{1F33A}','\u{1F337}','\u{1F338}','\u{1F340}','\u{1F341}',
  '\u{1F342}','\u{1F343}','\u{1F344}','\u{1F335}','\u{1F334}','\u{1F332}','\u{1F333}','\u{1F33E}',
  '\u{1F33F}','\u{2618}','\u{1F331}','\u{1F330}','\u{1F34E}','\u{1F34F}','\u{1F350}','\u{1F34A}',
  '\u{1F34B}','\u{1F34C}','\u{1F349}','\u{1F347}','\u{1F353}','\u{1F348}','\u{1F352}','\u{1F351}',
  '\u{1F34D}','\u{1F965}','\u{1F951}','\u{1F346}','\u{1F955}','\u{1F33D}','\u{1F336}','\u{1F952}',
  '\u{1F966}','\u{1F344}','\u{1F95C}','\u{1F950}','\u{1F35E}','\u{1F956}','\u{1F968}','\u{1F9C0}',
  '\u{1F356}','\u{1F357}','\u{1F969}','\u{1F354}','\u{1F35F}','\u{1F355}','\u{1F32D}','\u{1F32E}',
  '\u{1F32F}','\u{1F959}','\u{1F9C6}','\u{1F373}','\u{1F372}','\u{1F958}','\u{1F35D}','\u{1F35C}',
  '\u{1F363}','\u{1F364}','\u{1F365}','\u{1F960}','\u{1F370}','\u{1F382}','\u{1F36E}','\u{1F36D}',
  '\u{1F36C}','\u{1F36B}','\u{1F37F}','\u{1F369}','\u{1F366}','\u{2615}','\u{1F375}','\u{1F376}',
  '\u{1F37A}','\u{1F37E}','\u{1F943}','\u{1F964}','\u{26BD}','\u{1F3C0}','\u{1F3C8}','\u{26BE}',
  '\u{1F3BE}','\u{1F3D0}','\u{1F3C9}','\u{1F3B1}','\u{1F3D3}','\u{1F3F8}','\u{1F94A}','\u{1F945}',
  '\u{26F3}','\u{26F8}','\u{1F3A3}','\u{1F3BF}','\u{1F6F7}','\u{1F94C}','\u{1F3AF}','\u{1F3AE}',
  '\u{1F3B2}','\u{1F9E9}','\u{1F3A8}','\u{1F3AD}','\u{1F3A4}','\u{1F3A7}','\u{1F3B5}','\u{1F3B6}',
  '\u{1F3B8}','\u{1F3B9}','\u{1F941}','\u{1F3BA}','\u{1F3BB}','\u{1FA95}','\u{1F3AC}','\u{1F4F7}',
  '\u{1F4F9}','\u{1F3A0}','\u{1F3A1}','\u{1F3A2}','\u{1F680}','\u{1F681}','\u{1F6F8}','\u{1F6F0}',
  '\u{1F30D}','\u{1F30E}','\u{1F30F}','\u{1F311}','\u{1F312}','\u{1F313}','\u{1F314}','\u{1F315}',
  '\u{2B50}','\u{1F31F}','\u{26A1}','\u{1F525}','\u{1F4A7}','\u{1F30A}','\u{2744}','\u{2603}',
  '\u{1F308}','\u{2602}','\u{1F3E0}','\u{1F3E1}','\u{1F3D7}','\u{1F3D8}','\u{1F3DA}','\u{1F3F0}',
  '\u{1F3ED}','\u{1F5FC}','\u{1F5FD}','\u{1F5FF}','\u{26F2}','\u{26F5}','\u{1F6A2}','\u{1F685}',
  '\u{1F68C}','\u{1F697}','\u{1F695}','\u{1F6B2}','\u{1F6F4}','\u{1F6F5}','\u{1F6A8}','\u{1F6A6}',
  '\u{2693}','\u{1F6A7}','\u{26FD}','\u{1F9F2}','\u{1F9F0}','\u{1F527}','\u{1F528}','\u{1F529}',
  '\u{2699}','\u{1F4A1}','\u{1F50B}','\u{1F4BB}','\u{1F4F1}','\u{260E}','\u{1F4FA}','\u{1F4FB}',
  '\u{1F50D}','\u{1F56F}','\u{1F4D6}','\u{1F510}','\u{1F511}','\u{1F512}','\u{1F513}','\u{2764}',
  '\u{1F49B}','\u{1F49A}','\u{1F499}','\u{1F49C}','\u{1F5A4}','\u{1F90D}','\u{1F90E}','\u{1F49D}',
  '\u{1F48E}','\u{1F451}','\u{1F3A9}','\u{1F453}','\u{1F576}','\u{1F454}','\u{1F455}','\u{1F457}',
  '\u{1F458}','\u{1F9E4}','\u{1F9E3}','\u{1F9E5}','\u{1F9E6}','\u{1F45F}','\u{1F461}','\u{1F462}',
];

export function sasToEmoji(sasBytes) {
  if (!sasBytes || sasBytes.length < 4) return null;
  return [
    SAS_EMOJI[sasBytes[0]],
    SAS_EMOJI[sasBytes[1]],
    SAS_EMOJI[sasBytes[2]],
    SAS_EMOJI[sasBytes[3]],
  ];
}

// ─── Binary frame constants ─────────────────────────────────────────────────
const FRAME_AUDIO = 0x01;        // wire type tag (not the in-plaintext flags byte)
const FRAME_SAMPLES = 960;       // 20ms @ 48kHz
// Padding must be unpredictable, so it comes from the CSPRNG rather than zeros.
const randomBytes = (n) => nacl.randomBytes(n);
const BACKPRESSURE_LIMIT = 65536; // 64KB — drop frames if WS backlog exceeds this
const COMFORT_INTERVAL = 20;     // ms — comfort frame cadence (matches audio)
const COMFORT_THRESHOLD = 25;    // ms — send comfort if no audio sent within this

// ─── Tunnel class ────────────────────────────────────────────────────────────
export class AudioTunnel {
  constructor() {
    this._kp = null;          // our X25519 keypair
    this._shared = null;      // shared secret with peer
    this._sendPrefix = null;  // Uint8Array(20) — our nonce prefix
    this._recvPrefix = null;  // Uint8Array(20) — peer nonce prefix
    this._sendSeq = 0;        // our send counter
    this._replay = new ReplayWindow(); // anti-replay for received counters
    this._peerPubB64 = null;  // peer's current public key (detects re-key vs duplicate)
    this._leaving = false;    // deliberate leave — suppresses reconnect attempts
    this._sasBytes = null;    // Uint8Array(4) for SAS emoji
    this._room = null;        // room number — signed, so it can't be replayed elsewhere
    this._peerId = null;      // server-assigned peer id (userId or guest_<link>)
    this._peerIdentityB64 = null; // peer's Ed25519 identity key, once verified
    this._myIdentityB64 = null;   // identity we advertised (null = anonymous guest)
    this._authState = 'unknown';  // pinned | verified | tofu-new | new-device | anonymous | invalid
    this._audioCtx = null;
    this._capture = null;
    this._playback = null;
    this._stream = null;
    this._source = null;
    this._started = false;
    this._unsubs = [];        // wsService unsubscribe functions
    this._ws = null;          // raw WS (guest only)
    this._isGuest = false;
    this._lastSendTime = 0;   // timestamp of last frame sent (for comfort timer)
    this._comfortTimer = null; // setInterval handle for comfort frames

    // Codec (docs/protocol/audio-codec-v2.md). We start out claiming only what
    // every build has ever spoken, and upgrade once the browser has actually
    // confirmed it can run the Opus configuration — advertising a codec we turn
    // out not to have would be a connected call with silence on it.
    this._myCodecs = ['mulaw'];
    this._codec = 'mulaw';     // what we ENCODE with; receiving dispatches on length
    this._opusEnc = null;
    this._opusDec = null;
    this._codecsReady = isOpusSupported().then((ok) => {
      this._myCodecs = ok ? ['opus', 'mulaw'] : ['mulaw'];
      nlog('[TUNNEL] Codecs available: ' + this._myCodecs.join(','));
    }).catch(() => { this._myCodecs = ['mulaw']; });

    // External callbacks
    this.onPeerJoined = null; // (sasBytes: Uint8Array) => void
    this.onPeerLeft = null;
    this.onError = null;
    this.onSasVerify = null;  // (msg: { verified: boolean, by: string }) => void
    this.onPeerReconnecting = null; // peer's connection dropped, server holds the room
    this.onRekeyed = null;    // (sasBytes) => new DH after reconnect — SAS changed
    this.onAuthState = null;  // (state, info) => peer authentication result (ADR-014)
    this.onHeld = null;       // (state, sasBytes) => media withheld pending confirmation (SEC-05)
  }

  get authState() { return this._authState; }
  get peerIdentity() { return this._peerIdentityB64; }

  /** Called when the user confirms the SAS matched — records the key and marks it verified. */
  confirmPeerIdentity() {
    if (!this._peerIdentityB64 || !this._peerId) return false;
    // Pin first: since SEC-05 a new-device key is deliberately NOT recorded
    // at handshake time, so the person's confirmation is what writes it.
    tofu.pin(this._peerId, this._peerIdentityB64);
    const ok = tofu.markVerified(this._peerId, this._peerIdentityB64);
    if (ok) { this._authState = 'verified'; this.onAuthState?.('verified', { peerId: this._peerId }); }
    return ok;
  }

  /**
   * The person compared the SAS over another channel and said "это он" —
   * the only event allowed to start media on a held call (SEC-05,
   * call-lifecycle `held_untrusted`). Pins the key as verified when there
   * is one; an expected-but-anonymous peer has nothing to pin and stays a
   * decision the person just made with open eyes.
   */
  async confirmHeldPeer() {
    if (!this._held) return false;
    if (this._peerIdentityB64 && this._peerId) this.confirmPeerIdentity();
    this._held = false;
    await this._startAudio();
    this._ensureOpus();
    this.onPeerJoined?.(this._sasBytes);
    return true;
  }

  /** Fields every join/rejoin carries: our ephemeral key, signed by our identity. */
  _authFields(room) {
    const ephemeralPub = this._kp.publicKey;
    this._myIdentityB64 = identity.publicKeyB64();
    return {
      publicKey: b64enc(ephemeralPub),
      v: 2,
      identityKey: identity.publicKeyB64(),
      sig: idB64enc(identity.sign(room, ephemeralPub)),
      codecs: this._myCodecs,
    };
  }

  // ─── Shared room join logic (used by creator and direct calls) ──────────────
  _joinRoom(roomNumber, extraFields = {}) {
    // Guard: don't join tunnel if native E2EE call is already active.
    // When iOS app unlocks during native tunnel call, JS unfreezes and tries
    // to auto-join the tunnel. This creates a duplicate connection on the server
    // → when JS disconnects (getUserMedia fails), server sends tunnel_peer_left
    // to the remote peer → call dies.
    if (typeof window !== 'undefined' && window._nativeCallActive) {
      console.log('[TUNNEL] ⛔ Skipping tunnel_join: native call already active');
      this.onError?.('Native call already active');
      return;
    }

    this._kp = nacl.box.keyPair();
    this._isGuest = false;
    this._leaving = false;
    this._room = roomNumber;

    // Listen for tunnel control messages via wsService (JSON)
    this._unsubs.push(wsService.on('tunnel_joined', (msg) => {
      console.log(`[TUNNEL] Joined room ${msg.room}, ${msg.peers} peers`);
    }));
    this._unsubs.push(wsService.on('tunnel_peer', (msg) => {
      this._onPeerKey(msg);
    }));
    this._unsubs.push(wsService.on('tunnel_peer_left', () => {
      this._shared = null;
      this._peerPubB64 = null;
      this.onPeerLeft?.();
    }));
    // Peer's WS dropped — server holds its slot for the grace period.
    // Extend our silence watchdog so it doesn't kill the call meanwhile.
    this._unsubs.push(wsService.on('tunnel_peer_reconnecting', () => {
      nlog('[TUNNEL] Peer connection lost — waiting for reconnect');
      this._lastRecvTime = Date.now();
      this.onPeerReconnecting?.();
    }));
    // The server itself is going down for a deploy (SEC-ARCH-004). Not a
    // peer leaving: both sides rejoin when it is back and re-key. The
    // silence watchdog is given the restart's grace so it does not end the
    // call in the meantime.
    this._unsubs.push(wsService.on('tunnel_server_restart', (msg) => {
      nlog('[TUNNEL] Server restarting — holding the call');
      this._graceUntil = Date.now() + (Number(msg?.graceMs) || 30000);
      this.onPeerReconnecting?.();
    }));
    // Our own WS reconnected (wsService auto-reconnects after 3s):
    // rejoin the room with a FRESH keypair → both sides re-key.
    this._unsubs.push(wsService.on('ws_reconnected', () => {
      if (this._leaving || !this._started) return;
      nlog('[TUNNEL] WS reconnected — rejoining tunnel with fresh keypair');
      this._kp = nacl.box.keyPair();
      this._shared = null;
      this._peerPubB64 = null;
      // Await the codec probe so the rejoin advertises the same list as the
      // original join; handlers above are already registered, so nothing is
      // missed while it settles.
      this._codecsReady.then(() => wsService.send({
        type: 'tunnel_join',
        room: roomNumber,
        ...this._authFields(roomNumber),
        ...extraFields,
      }));
    }));
    this._unsubs.push(wsService.on('tunnel_error', (msg) => {
      this.onError?.(msg.message);
    }));
    this._unsubs.push(wsService.on('sas_verify', (msg) => {
      this.onSasVerify?.(msg);
    }));

    // Listen for binary audio frames via wsService
    this._unsubs.push(wsService.on('tunnel_audio_binary', (data) => {
      this._onAudioBinary(data);
    }));

    this._codecsReady.then(() => wsService.send({
      type: 'tunnel_join',
      room: roomNumber,
      ...this._authFields(roomNumber),
      ...extraFields,
    }));
  }

  // ─── Creator: join via existing wsService (link-based calls) ───────────────
  joinAsCreator(roomNumber) {
    this._joinRoom(roomNumber);
  }

  // ─── Direct call: join via existing wsService (contact-to-contact E2EE) ────
  joinAsDirect(roomNumber, directCallId) {
    // Between accounts, the peer MUST present an identity: this is what
    // separates "звонок другу" from an anonymous link visit, and what arms
    // the held_untrusted state below. Guests never set it — anonymity is
    // their design, not their failure.
    this._expectIdentity = true;
    this._joinRoom(roomNumber, { directCallId });
  }

  // ─── Guest: open dedicated WS with tunnel token ───────────────────────────
  joinAsGuest(tunnelToken) {
    this._isGuest = true;
    this._leaving = false;
    return this._openGuestWs(tunnelToken);
  }

  _openGuestWs(tunnelToken, room) {
    this._kp = nacl.box.keyPair(); // fresh keypair per connection (nonce safety)
    if (room !== undefined) this._room = room;
    // Guests stay anonymous on purpose: a stable device key would let a link
    // owner correlate the same visitor across links (and with their account).
    this._myIdentityB64 = null;

    return new Promise((resolve, reject) => {
      const url = `${serverWsBase()}/ws`;
      this._ws = new WebSocket(url);
      this._ws.binaryType = 'arraybuffer';

      const timeout = setTimeout(() => reject(new Error('Tunnel timeout')), 10000);

      this._ws.onopen = () => {
        clearTimeout(timeout);
        // A guest has no account, so no identity key — the call is anonymous by
        // design (holding the link is the consent). We still sign nothing rather
        // than pretending: the owner's client shows "not authenticated".
        this._codecsReady.then(() => this._ws?.send(JSON.stringify({
          type: 'tunnel_guest_auth',
          tunnelToken,
          publicKey: b64enc(this._kp.publicKey),
          v: 2,
          codecs: this._myCodecs,
        })));
        resolve();
      };

      this._ws.onmessage = (e) => {
        // Binary frame = tunnel audio
        if (e.data instanceof ArrayBuffer) {
          const bytes = new Uint8Array(e.data);
          if (bytes[0] === FRAME_AUDIO) {
            this._onAudioBinary(bytes.subarray(1));
          }
          return;
        }
        // JSON control messages
        try {
          const msg = JSON.parse(e.data);
          if (msg.type === 'tunnel_joined') {
            // The room number is part of the signed payload, and a guest only
            // holds an opaque token — learn it from the server.
            if (msg.room !== undefined) this._room = msg.room;
            console.log(`[TUNNEL] Guest joined, ${msg.peers} peers`);
          }
          else if (msg.type === 'tunnel_peer') this._onPeerKey(msg);
          else if (msg.type === 'tunnel_peer_left') { this._shared = null; this._peerPubB64 = null; this.onPeerLeft?.(); }
          else if (msg.type === 'tunnel_peer_reconnecting') {
            nlog('[TUNNEL] Peer connection lost — waiting for reconnect');
            this._lastRecvTime = Date.now();
            this.onPeerReconnecting?.();
          }
          else if (msg.type === 'tunnel_error') { this.onError?.(msg.message); }
          else if (msg.type === 'sas_verify') { this.onSasVerify?.(msg); }
          else if (msg.type === 'tunnel_server_restart') {
            nlog('[TUNNEL] Server restarting — holding the call');
            this._graceUntil = Date.now() + (Number(msg.graceMs) || 30000);
            this.onPeerReconnecting?.();
          }
        } catch (err) { /* ignore parse errors */ }
      };

      this._ws.onclose = () => {
        // Mid-call network blip: reconnect with a fresh keypair while the
        // server holds our room slot (grace period). Deliberate leave or
        // pre-call close falls through to onPeerLeft as before.
        if (!this._leaving && this._started) {
          this._attemptGuestReconnect(tunnelToken);
        } else {
          this._shared = null;
          this.onPeerLeft?.();
        }
      };
      this._ws.onerror = () => reject(new Error('Tunnel WS error'));
    });
  }

  async _attemptGuestReconnect(tunnelToken) {
    this._shared = null;
    this._peerPubB64 = null;
    this.onPeerReconnecting?.();
    // A window, not a count: three tries covered a network blip and not a
    // server restart, which takes longer than four seconds to come back.
    const deadline = Date.now() + RECONNECT_WINDOW_MS;
    this._graceUntil = deadline;
    for (let attempt = 1; Date.now() < deadline && !this._leaving; attempt++) {
      nlog(`[TUNNEL] Guest reconnect attempt ${attempt}`);
      await new Promise(r => setTimeout(r, attempt === 1 ? 300 : 1500));
      if (this._leaving) return;
      try {
        await this._openGuestWs(tunnelToken);
        nlog('[TUNNEL] Guest WS reconnected — waiting for re-key');
        this._lastRecvTime = Date.now();
        return; // re-key arrives via tunnel_peer
      } catch (err) { /* next attempt */ }
    }
    if (!this._leaving) {
      nlog('[TUNNEL] Guest reconnect failed — giving up');
      this.onPeerLeft?.();
    }
  }

  // ─── Key exchange & nonce derivation ──────────────────────────────────────
  async _onPeerKey(msg) {
    // Back-compat: older call sites passed the bare base64 key.
    const peerMsg = typeof msg === 'string' ? { publicKey: msg } : (msg || {});
    const b64Key = peerMsg.publicKey;
    if (!b64Key) return;

    // Same key again = duplicate delivery. A DIFFERENT key while established
    // means the peer reconnected with a fresh keypair — re-derive everything.
    if (this._shared && this._peerPubB64 === b64Key) return;
    const isRekey = !!this._shared;
    const peerPub = new Uint8Array(b64dec(b64Key));

    // ─── ADR-014: authenticate the peer BEFORE deriving anything ────────────
    // A relay that swaps the ephemeral key cannot produce a matching signature,
    // so an invalid signature aborts the call instead of downgrading silently.
    if (peerMsg.peerId) this._peerId = String(peerMsg.peerId);
    if (this._room == null && peerMsg.room !== undefined) this._room = peerMsg.room;
    const auth = authenticatePeer({
      room: this._room,
      ephemeralPub: peerPub,
      identityPub: peerMsg.identityKey ? b64dec(peerMsg.identityKey) : null,
      sig: peerMsg.sig ? b64dec(peerMsg.sig) : null,
      peerId: this._peerId,
      tofu,
    });
    if (!auth.ok) {
      nlog('[TUNNEL] ⛔ Peer signature invalid — aborting handshake');
      this._authState = 'invalid';
      this.onAuthState?.('invalid', { peerId: this._peerId });
      this.onError?.('Не удалось подтвердить собеседника — звонок остановлен');
      this.leave();
      return;
    }
    // A re-key keeps the identity or ends the call. The first exchange holds
    // an unconfirmed identity for the person (SEC-05 below); a re-key skips
    // that hold — and would have let a peer with a different, validly
    // signed identity in at the reconnect, which is exactly the window an
    // interposer gets by cutting the socket. There is no honest reason for
    // the same call's peer to change keys: refuse, do not ask.
    if (isRekey && (this._peerIdentityB64 || null) !== (auth.identityB64 || null)) {
      nlog('[TUNNEL] ⛔ Peer identity changed mid-call — aborting');
      this._authState = 'invalid';
      this.onError?.('Собеседник сменил ключ посреди звонка — звонок остановлен');
      this.leave();
      return;
    }
    this._peerIdentityB64 = auth.identityB64;
    this._authState = auth.verified ? 'verified' : auth.state;
    this.onAuthState?.(this._authState, { peerId: this._peerId, identity: auth.identityB64 });

    this._peerPubB64 = b64Key;
    nlog(isRekey
      ? `[TUNNEL] Peer reconnected with new key — re-keying (auth: ${this._authState})`
      : `[TUNNEL] Peer key received — computing shared secret (auth: ${this._authState})`);
    this._shared = nacl.box.before(peerPub, this._kp.secretKey);

    // Derive direction-separated nonce prefixes + SAS. v2 mixes both identity
    // keys in, so two sides disagreeing on identity derive different keys and
    // simply cannot talk; v1 keeps old native builds working.
    let session;
    try {
      session = deriveSession({
        shared: this._shared,
        myEphemeral: this._kp.publicKey,
        peerEphemeral: peerPub,
        myIdentity: this._myIdentityB64 ? b64dec(this._myIdentityB64) : null,
        peerIdentity: auth.identityB64 ? b64dec(auth.identityB64) : null,
        v2: Number(peerMsg.v) >= 2,
      });
    } catch (err) {
      nlog('[TUNNEL] ⛔ Key derivation refused: ' + err.message);
      this._shared = null;
      this.onError?.(err.message);
      this.leave();
      return;
    }
    this._sendPrefix = session.sendPrefix;
    this._recvPrefix = session.recvPrefix;
    this._sasBytes = session.sasBytes;
    this._sendSeq = 0;
    this._replay.reset();
    console.log('[TUNNEL] Nonce prefixes derived, SAS ready');

    // Both peers run this with mirrored arguments and reach the same answer
    // from one fixed preference order, so no extra round trip is needed and an
    // asymmetric result — each side encoding what the other cannot decode — is
    // not representable. Receiving still dispatches on plaintext length.
    const codec = selectCodec(this._myCodecs, peerMsg.codecs);
    if (codec !== this._codec) {
      this._codec = codec;
      this._teardownOpus();   // a re-key may land us on a different codec
    }
    nlog('[TUNNEL] Codec: ' + this._codec + ' (peer offered ' + (peerMsg.codecs || ['mulaw']).join(',') + ')');

    // ─── SEC-05: fail-closed on a changed or missing identity ───────────────
    // An INVALID signature has always aborted above. But a VALID signature
    // under a key this person never had before ('new-device'), or no
    // identity at all where one was expected, used to warn while the audio
    // already flowed — forgery was blocked, substitution was not. Here the
    // handshake is complete and the SAS exists, so there is something to
    // compare — and nothing has been said yet. Media waits for the person.
    // First contact ('tofu-new') passes: the same TOFU bet the chat makes.
    const suspicious = this._authState === 'new-device' || this._authState === 'anonymous';
    if (!isRekey && this._expectIdentity && suspicious) {
      this._held = true;
      nlog(`[TUNNEL] ⛔ media withheld — peer identity is ${this._authState}, waiting for the person (SEC-05)`);
      this.onHeld?.(this._authState, this._sasBytes);
      return;
    }

    // Await audio pipeline — must be ready before signaling onPeerJoined
    // (no-op on re-key: pipeline keeps running through the reconnect)
    await this._startAudio();
    this._ensureOpus();
    if (isRekey) {
      this._lastRecvTime = Date.now(); // fresh watchdog window after re-key
    this._graceUntil = 0;
      this.onRekeyed?.(this._sasBytes);
    } else {
      this.onPeerJoined?.(this._sasBytes);
    }
  }

  // ─── Audio pipeline setup ─────────────────────────────────────────────────
  async _startAudio() {
    if (this._started) return;
    this._started = true;

    try {
      nlog('[TUNNEL] Requesting getUserMedia...');
      this._stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
      });
      nlog('[TUNNEL] getUserMedia OK, tracks: ' + this._stream.getAudioTracks().length);
      // Guard: connection may have been cleaned up during getUserMedia prompt
      if (!this._shared) { nlog('[TUNNEL] Cleaned up during getUserMedia'); return; }

      this._audioCtx = new AudioContext({ sampleRate: 48000 });
      // Android WebView may start AudioContext in 'suspended' state — resume explicitly
      if (this._audioCtx.state === 'suspended') {
        nlog('[TUNNEL] AudioContext suspended, resuming...');
        await this._audioCtx.resume();
      }
      nlog('[TUNNEL] AudioContext state: ' + this._audioCtx.state + ' sampleRate: ' + this._audioCtx.sampleRate);
      // PUBLIC_URL, not a bare absolute path: the embedded shells serve the
      // page from /www/ (Android) or file:// (iOS), where '/audio-worklet.js'
      // names a file that does not exist — the first real call from the
      // embedded Pixel died here with "Unable to load a worklet's module".
      await this._audioCtx.audioWorklet.addModule(`${process.env.PUBLIC_URL || ''}/audio-worklet.js`);
      nlog('[TUNNEL] AudioWorklet loaded');
      // Guard: connection may have been cleaned up during worklet load
      if (!this._shared || !this._audioCtx) { nlog('[TUNNEL] Cleaned up during worklet load'); return; }

      // Capture: mic -> worklet -> mu-law -> encrypt -> binary WS
      this._source = this._audioCtx.createMediaStreamSource(this._stream);
      this._capture = new AudioWorkletNode(this._audioCtx, 'pcm-capture');
      this._captureCount = 0;
      this._capture.port.onmessage = (e) => {
        if (!this._shared || !this._sendPrefix) return;
        const f32 = new Float32Array(e.data);
        if (this._captureCount < 5) {
          const maxVal = f32.reduce((m, v) => Math.max(m, Math.abs(v)), 0).toFixed(3);
          nlog('[TUNNEL] Capture frame ' + this._captureCount + ' samples:' + f32.length + ' max:' + maxVal);
        }
        this._captureCount++;
        // Capture logged its first five frames and then went quiet, which is
        // the moment before anyone has said anything. A microphone that dies
        // mid-call looked exactly like one that worked.
        this._meter('cap', f32);
        this._sendChunk(f32);
      };
      this._source.connect(this._capture);
      // Connect to destination so worklet processes (output is silence)
      const silencer = this._audioCtx.createGain();
      silencer.gain.value = 0;
      this._capture.connect(silencer);
      silencer.connect(this._audioCtx.destination);

      // Playback: binary WS -> decrypt -> mu-law decode -> worklet -> speakers
      this._playback = new AudioWorkletNode(this._audioCtx, 'pcm-playback');
      this._playback.connect(this._audioCtx.destination);

      // Comfort frame timer: sends padding when no audio (mute/silence) for DPI resistance
      this._lastSendTime = Date.now();
      this._comfortTimer = setInterval(() => {
        if (!this._shared) return;
        if (Date.now() - this._lastSendTime > COMFORT_THRESHOLD) {
          this._sendComfort();
        }
      }, COMFORT_INTERVAL);

      // Silence detection: if no audio received for 15s, assume peer disconnected
      this._lastRecvTime = 0;
      this._silenceTimer = setInterval(() => {
        if (this._graceUntil && Date.now() < this._graceUntil) return;   // a restart or reconnect in progress
        if (this._lastRecvTime && Date.now() - this._lastRecvTime > 15000) {
          console.warn('[TUNNEL] No audio received for 15s — peer likely disconnected');
          clearInterval(this._silenceTimer);
          this._silenceTimer = null;
          this.onPeerLeft?.();
        }
      }, 5000);

      nlog('[TUNNEL] Audio pipeline started (with DPI padding)');
    } catch (err) {
      nlog('[TUNNEL] Audio init FAILED: ' + err.message);
      this.onError?.(err.message);
    }
  }

  // ─── Opus lifecycle ────────────────────────────────────────────────────────

  /**
   * Report what is actually going through, every 50 frames — one second.
   *
   * iOS has had this since ADR-010 and it is why an iPhone's audio problems
   * are answerable from a log. On this side the pipeline said "Recv audio
   * frame #0..#2" and then nothing, so "I could not hear them" and "they sent
   * silence" and "it decoded but never reached the speaker" were three
   * indistinguishable states — which is exactly the position we were in.
   *
   * RMS is on the 0..1 float scale the worklet uses; ~0.001 is a quiet room,
   * ~0.05 is speech at arm's length, 0 for many seconds is a dead pipe.
   */
  _meter(which, f32) {
    const m = this._meters || (this._meters = {});
    const s = m[which] || (m[which] = { n: 0, sum: 0, peak: 0, total: 0, allPeak: 0, loud: 0 });
    let acc = 0, peak = 0;
    for (let i = 0; i < f32.length; i++) {
      const v = f32[i] < 0 ? -f32[i] : f32[i];
      acc += v * v;
      if (v > peak) peak = v;
    }
    const frameRms = Math.sqrt(acc / (f32.length || 1));
    s.sum += acc / (f32.length || 1);
    if (peak > s.peak) s.peak = peak;
    // Kept for the whole call, so the verdict survives a log buffer that has
    // rotated away the second-by-second lines — which is exactly what happened
    // to the one call where the microphone was dead.
    s.total++;
    if (peak > s.allPeak) s.allPeak = peak;
    if (frameRms > 0.005) s.loud++;   // roughly: someone was speaking
    if (++s.n >= 50) {
      const rms = Math.sqrt(s.sum / s.n);
      nlog(`[TUNNEL] ${which} 50 frames rms=${rms.toFixed(4)} peak=${s.peak.toFixed(4)}`
         + (which === 'dec' ? ` codec=${this._codec}` : ''));
      s.n = 0; s.sum = 0; s.peak = 0;
    }
  }

  /** Build the Opus codec pair once the pipeline exists. No-op for mu-law. */
  _ensureOpus() {
    if (this._codec !== 'opus' || this._opusEnc) return;
    const onErr = (e) => {
      // WebCodecs said it could do this and then could not. Rather than lose
      // the call, drop to mu-law: the peer dispatches on plaintext length, so
      // it follows us without renegotiating.
      nlog('[TUNNEL] Opus failed (' + (e?.message || e) + ') — falling back to mu-law');
      this._codec = 'mulaw';
      this._teardownOpus();
    };
    this._opusEnc = createOpusEncoder((packet) => this._emitAudio(packet), onErr);
    this._opusDec = createOpusDecoder((f32) => {
      this._meter('dec', f32);
      this._playback?.port.postMessage(f32.buffer, [f32.buffer]);
    }, onErr);
    if (!this._opusEnc || !this._opusDec) onErr(new Error('codec unavailable'));
  }

  _teardownOpus() {
    this._opusEnc?.close(); this._opusEnc = null;
    this._opusDec?.close(); this._opusDec = null;
  }

  // ─── Send: PCM -> encode -> pad to a constant size -> encrypt -> WS ────────
  _sendChunk(f32) {
    if (!this._shared || !this._sendPrefix) return;
    if (this._codec === 'opus' && this._opusEnc) {
      // Mark the pipeline busy HERE, not when the packet finally leaves.
      // WebCodecs encodes asynchronously, so _lastSendTime would stay stale for
      // tens of milliseconds while real speech was already in flight — long
      // enough for the comfort timer to decide the line was idle and push a
      // silent frame through the SAME encoder. Speech came out interleaved with
      // silence, which is heard as heavy crackling.
      this._lastSendTime = Date.now();
      this._opusEnc.encode(f32);
      return;
    }
    this._emitAudio(encodeMulaw(f32toI16(f32)));
  }

  /** Wrap an encoded payload in the constant-size plaintext and send it. */
  _emitAudio(payload) {
    if (!this._shared || !this._sendPrefix) return;
    try {
      this._sendFrame(buildPlaintext(this._codec, payload, FLAG_AUDIO, randomBytes));
    } catch (e) {
      nlog('[TUNNEL] Frame build failed: ' + e.message);
    }
  }

  // ─── Comfort frame: constant traffic during silence/mute (DPI resistance) ──
  _sendComfort() {
    if (!this._shared || !this._sendPrefix) return;
    if (this._codec === 'opus' && this._opusEnc) {
      // Second guard, independent of the clock: if the encoder still holds a
      // frame, the line is not idle no matter what the timestamp says, and
      // pushing silence in would interleave it with speech.
      if (this._opusEnc.pending() > 0) return;
      // Encode real silence rather than sending an empty payload: Opus is
      // stateful, and a hole in the stream costs the far decoder its
      // continuity. The wire size is the same either way.
      this._opusEnc.encode(new Float32Array(FRAME_SAMPLES));
      return;
    }
    this._sendFrame(buildPlaintext('mulaw', null, FLAG_COMFORT, randomBytes));
  }

  // ─── Common frame sender with backpressure check ───────────────────────────
  _sendFrame(plaintext) {
    // Backpressure: drop frame if WS send buffer is overloaded (HOL mitigation)
    if (this._isGuest) {
      if (!this._ws || this._ws.readyState !== WebSocket.OPEN) return;
      if (this._ws.bufferedAmount > BACKPRESSURE_LIMIT) return;
    }

    const counter = this._sendSeq++;
    const nonce = buildNonce(this._sendPrefix, counter);
    const enc = nacl.secretbox(plaintext, nonce, this._shared);

    // Wire frame: [0x01 tag][counter 4B LE][ciphertext 1040B] = 1045B
    const frame = new Uint8Array(1 + 4 + enc.length);
    frame[0] = FRAME_AUDIO;
    frame[1] = counter & 0xff;
    frame[2] = (counter >> 8) & 0xff;
    frame[3] = (counter >> 16) & 0xff;
    frame[4] = (counter >> 24) & 0xff;
    frame.set(enc, 5);

    if (this._isGuest) {
      this._ws.send(frame.buffer);
    } else {
      wsService.sendBinary(frame.buffer);
    }
    this._lastSendTime = Date.now();
  }

  // ─── Receive: binary frame -> decrypt -> check flags -> mu-law -> playback ─
  _onAudioBinary(data) {
    if (!this._shared || !this._playback || !this._recvPrefix) return;
    this._lastRecvTime = Date.now();
    if (!this._recvCount) this._recvCount = 0;
    if (this._recvCount < 3) nlog('[TUNNEL] Recv audio frame #' + this._recvCount + ' len:' + data.length);
    this._recvCount++;
    try {
      // data: Uint8Array, starts with 4-byte counter (type tag already stripped)
      const counter = data[0] | (data[1] << 8) | (data[2] << 16) | (data[3] << 24);
      const cipher = data.subarray(4);
      const nonce = buildNonce(this._recvPrefix, counter);
      const plain = nacl.secretbox.open(cipher, nonce, this._shared);
      if (!plain) {
        if (counter < 3) console.warn('[TUNNEL] Decrypt failed, counter=', counter);
        return;
      }

      // Anti-replay: only after authentication, so forged counters can't poison the window
      if (!this._replay.accept(counter)) return;

      // The decoder comes from the plaintext length, not from what we
      // negotiated: a frame that outraces `tunnel_peer`, or a peer that
      // disagrees with us, is still heard instead of dropped.
      const frame = parsePlaintext(plain);
      if (!frame) return;

      if (frame.codec === 'opus') {
        // Comfort frames carry encoded silence and are decoded like any other,
        // so the decoder never loses continuity.
        if (!this._opusDec) this._ensureOpus();
        this._opusDec?.decode(frame.payload);
        return;
      }

      if (frame.flags === FLAG_COMFORT) return;   // v1 comfort frame is a hole
      const f32 = i16toF32(decodeMulaw(frame.payload));
      this._meter('dec', f32);
      this._playback.port.postMessage(f32.buffer, [f32.buffer]);
    } catch (e) { console.warn('[TUNNEL] Audio decode error:', e); }
  }

  sendSasVerify(verified) {
    const msg = { type: 'sas_verify', verified };
    if (this._isGuest && this._ws?.readyState === WebSocket.OPEN) {
      this._ws.send(JSON.stringify(msg));
    } else {
      wsService.send(msg);
    }
  }

  setMuted(muted) {
    this._stream?.getAudioTracks().forEach(t => { t.enabled = !muted; });
  }

  leave() {
    this._leaving = true;
    const msg = { type: 'tunnel_leave' };
    if (this._isGuest && this._ws?.readyState === WebSocket.OPEN) {
      this._ws.send(JSON.stringify(msg));
    } else {
      wsService.send(msg);
    }
    this._cleanup();
  }

  /**
   * One line that says whether this call had audio, in each direction.
   *
   * The per-second lines are the detail; this is the verdict, and it is here
   * because logcat is a ring buffer. A call where the microphone was dead
   * scrolled out of it before anyone came looking, leaving the question
   * unanswerable for the second time in two days.
   */
  _logAudioSummary() {
    const m = this._meters;
    if (!m) return;
    const part = (k, label) => {
      const s = m[k];
      if (!s) return `${label}=никогда не запускался`;
      const pct = s.total ? Math.round((s.loud / s.total) * 100) : 0;
      return `${label}: frames=${s.total} peak=${s.allPeak.toFixed(4)} loud=${pct}%`;
    };
    nlog(`[TUNNEL] Итог по звуку — ${part('cap', 'микрофон')} · ${part('dec', 'приём')} · codec=${this._codec}`);
    this._meters = null;
  }

  _cleanup() {
    this._logAudioSummary();
    this._started = false;
    this._shared = null;
    this._sendPrefix = null;
    this._recvPrefix = null;
    this._sendSeq = 0;
    this._replay.reset();
    this._peerPubB64 = null;
    this._sasBytes = null;
    this._lastSendTime = 0;
    if (this._comfortTimer) { clearInterval(this._comfortTimer); this._comfortTimer = null; }
    this._teardownOpus();
    if (this._silenceTimer) { clearInterval(this._silenceTimer); this._silenceTimer = null; }
    this._unsubs.forEach(fn => fn());
    this._unsubs = [];
    if (this._capture) { try { this._capture.disconnect(); } catch (e) {} this._capture = null; }
    if (this._source) { try { this._source.disconnect(); } catch (e) {} this._source = null; }
    if (this._playback) { try { this._playback.disconnect(); } catch (e) {} this._playback = null; }
    if (this._stream) { this._stream.getTracks().forEach(t => t.stop()); this._stream = null; }
    if (this._audioCtx) { this._audioCtx.close().catch(() => {}); this._audioCtx = null; }
    if (this._isGuest && this._ws) { try { this._ws.close(); } catch (e) {} this._ws = null; }
  }
}
